import {
  Box,
  Card,
  CardMedia,
  Chip,
  Container,
  Dialog,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
  Stack,
  Fade,
} from "@mui/material";
import {
  Close,
  ChevronLeft,
  ChevronRight,
  Collections,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import useFetchDataWithPagination from "../../../utility/useFetchDataWithPagination";

const GalleryViewer = () => {
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [openLightbox, setOpenLightbox] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { data, isLoading, paginate } = useFetchDataWithPagination("gallery");
  const [galleryList, setGalleryList] = useState([]);

  useEffect(() => {
    if (data?.data?.data) {
      setGalleryList(data.data.data.data);
    }
  }, [data]);

  const filteredGallery = Array.isArray(galleryList)
    ? galleryList.filter(
        (item) => selectedFilter === "all" || item.category === selectedFilter
      )
    : [];

  const categories = Array.isArray(galleryList)
    ? [...new Set(galleryList.map((item) => item.category).filter(Boolean))]
    : [];

  const openImage = (index) => {
    setCurrentImageIndex(index);
    setOpenLightbox(true);
  };

  const handleNext = () => {
    setCurrentImageIndex((prev) =>
      prev < filteredGallery.length - 1 ? prev + 1 : 0
    );
  };

  const handlePrev = () => {
    setCurrentImageIndex((prev) =>
      prev > 0 ? prev - 1 : filteredGallery.length - 1
    );
  };

  const handleKeyPress = (e) => {
    if (e.key === "ArrowRight") handleNext();
    if (e.key === "ArrowLeft") handlePrev();
    if (e.key === "Escape") setOpenLightbox(false);
  };

  useEffect(() => {
    if (openLightbox) {
      window.addEventListener("keydown", handleKeyPress);
      return () => window.removeEventListener("keydown", handleKeyPress);
    }
  }, [openLightbox, currentImageIndex]);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {isLoading && <LinearProgress color="primary" sx={{ mb: 2 }} />}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 4 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Collections color="primary" sx={{ fontSize: 40 }} />
          <Typography variant="h4" fontWeight={700} color="primary">
            Galerie Photos
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip
            label="Tout"
            color={selectedFilter === "all" ? "primary" : "default"}
            onClick={() => setSelectedFilter("all")}
            sx={{ cursor: "pointer" }}
          />
          {categories.map((cat, idx) => (
            <Chip
              key={idx}
              label={cat}
              color={selectedFilter === cat ? "primary" : "default"}
              onClick={() => setSelectedFilter(cat)}
              sx={{ cursor: "pointer" }}
            />
          ))}
        </Stack>
      </Stack>

      <Grid container spacing={2}>
        {filteredGallery.map((item, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            <Fade in timeout={300}>
              <Card
                sx={{
                  cursor: "pointer",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "scale(1.03)",
                    boxShadow: 8,
                  },
                }}
                onClick={() => openImage(index)}
              >
                <CardMedia
                  component="img"
                  height="250"
                  image={item.image}
                  alt={item.title}
                  sx={{ objectFit: "cover" }}
                />
                <Box sx={{ p: 2 }}>
                  <Typography variant="h6" noWrap>
                    {item.title}
                  </Typography>
                  {item.category && (
                    <Chip
                      label={item.category}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
              </Card>
            </Fade>
          </Grid>
        ))}
      </Grid>

      {filteredGallery.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            textAlign: "center",
            py: 8,
            bgcolor: "grey.50",
            borderRadius: 2,
          }}
        >
          <Collections sx={{ fontSize: 80, color: "grey.300", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Aucune photo disponible
          </Typography>
        </Paper>
      )}

      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        {paginate()}
      </Box>

      <Dialog
        open={openLightbox}
        onClose={() => setOpenLightbox(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "rgba(0,0,0,0.95)",
            boxShadow: "none",
          },
        }}
      >
        <IconButton
          onClick={() => setOpenLightbox(false)}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            color: "white",
            bgcolor: "rgba(255,255,255,0.1)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
            zIndex: 1,
          }}
        >
          <Close />
        </IconButton>

        <Box sx={{ position: "relative", p: 2 }}>
          <Box
            component="img"
            src={filteredGallery[currentImageIndex]?.image}
            alt={filteredGallery[currentImageIndex]?.title}
            sx={{
              width: "100%",
              maxHeight: "80vh",
              objectFit: "contain",
            }}
          />

          <IconButton
            onClick={handlePrev}
            sx={{
              position: "absolute",
              left: 20,
              top: "50%",
              transform: "translateY(-50%)",
              color: "white",
              bgcolor: "rgba(255,255,255,0.1)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
            }}
          >
            <ChevronLeft fontSize="large" />
          </IconButton>

          <IconButton
            onClick={handleNext}
            sx={{
              position: "absolute",
              right: 20,
              top: "50%",
              transform: "translateY(-50%)",
              color: "white",
              bgcolor: "rgba(255,255,255,0.1)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
            }}
          >
            <ChevronRight fontSize="large" />
          </IconButton>

          <Box
            sx={{
              position: "absolute",
              bottom: 20,
              left: "50%",
              transform: "translateX(-50%)",
              bgcolor: "rgba(0,0,0,0.7)",
              color: "white",
              px: 3,
              py: 1,
              borderRadius: 2,
            }}
          >
            <Typography variant="h6">
              {filteredGallery[currentImageIndex]?.title}
            </Typography>
            {filteredGallery[currentImageIndex]?.description && (
              <Typography variant="body2" color="grey.300">
                {filteredGallery[currentImageIndex]?.description}
              </Typography>
            )}
            <Typography variant="caption" color="grey.400">
              {currentImageIndex + 1} / {filteredGallery.length}
            </Typography>
          </Box>
        </Box>
      </Dialog>
    </Container>
  );
};

export default GalleryViewer;